import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

@Component({
  name: 'CmgModal',
})
export default class CmgModal extends Vue {
  @Prop({ required: true, default: true, type: Boolean })
  visible!: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  fullScreen!: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  shimmerTop!: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  shimmerBottom?: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  closeCircleIcon?: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  isEmpty?: boolean;

  @Prop({ required: false, type: String })
  imgSrc?: string;

  @Prop({ required: false, default: 'beige', type: String })
  background!: string;

  @Prop({ required: false, type: String })
  manualWidth?: string;

  @Prop({ default: false, type: Boolean })
  fullWidth!: boolean;

  @Prop({ default: false, type: Boolean })
  fullHeight!: boolean;

  @Prop({ default: true, type: Boolean })
  isTopLevelModal!: boolean;

  @Prop({ default: true, type: Boolean })
  blur!: boolean;

  @Prop({ default: false, type: Boolean })
  clearBackground!: boolean;

  @Prop({ required: false, default: '', type: String })
  ariaLabel!: string;

  @Prop({ required: false, default: '', type: String })
  ariaRole!: string;

  @Prop({ required: false, default: '', type: String })
  id!: string;

  @Prop({ required: false, default: '', type: String })
  formType!: string;

  @Prop({ required: false, default: '', type: String })
  backgroundImageUrl!: string;

  @Prop({ required: false, default: false, type: Boolean })
  noTitleMargin!: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  unmaskContent!: boolean;

  @Prop({ required: false, default: false, type: Boolean })
  isFACModal!: boolean;

  @Watch('visible')
  watchVisibility(visible: boolean) {
    if (this.blur) {
      this.$services.ui.setAppBlur(visible);
    }

    if (this.isTopLevelModal) {
      this.$services.ui.setAppScrollability(!visible);
    } 
  }

  get hasHeader(): boolean {
    return !!this.$scopedSlots['header'];
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get closeCircleButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.closeCircle.default;
  }

  mounted() {
    if (this.isTopLevelModal) {
      document.body.addEventListener('keyup', this.closeModalOnEscapeEventHandler);
      document.addEventListener('keydown', this.tabModalOnFocusEventHandler);
    }

    if (this.fullHeight) {
      this.setFullHeight();
      window.addEventListener('resize', this.setFullHeight);
    }
  }

  destroyed() {
    if (this.isTopLevelModal) {
      this.$services.ui.setAppScrollability(true);
      document.body.removeEventListener('keyup', this.closeModalOnEscapeEventHandler);
    }
    
    window.removeEventListener('resize', this.setFullHeight);
  }

  setFullHeight() {
    const defaultFontSize = Number((window as any).getComputedStyle(document.body)
      .getPropertyValue('font-size')
      .match(/\d+/)[0]);
    
    const heightInRem = (window.innerHeight / defaultFontSize).toFixed(3);

    if (this.id) {
      const modal = document.getElementById(this.id);

      if (modal) {
        modal.setAttribute('style', `height:${heightInRem}rem`);
      }
    }
  }

  tabModalOnFocusEventHandler(event: KeyboardEvent) {
    const classCheck = document.getElementsByClassName('cmg-modal-container');
    if (classCheck.length > 0) {
      const focusableEls = document.getElementsByClassName('cmg-modal-container')[classCheck.length - 1].querySelectorAll('[tabindex="0"]');
      const firstFocusableEl = focusableEls[0] as HTMLElement;
      const lastFocusableEl = focusableEls[focusableEls.length - 1] as HTMLElement;
      if (event.key === 'Tab' && event.shiftKey) {
        if (document.activeElement === firstFocusableEl) {
          event.preventDefault();
          (lastFocusableEl as HTMLElement).focus();
        } // Check for Tab
      } else if (event.key === 'Tab') {
        if (document.activeElement === lastFocusableEl) {
          event.preventDefault();
          (firstFocusableEl as HTMLElement).focus();
        }
      }
    }
  }

  close() {
    this.$emit('close');
    this.$services.ui.focusBack();

    if(this.$appStore.state.ui.isFromRewards) {
      this.$services.ui.setIsFromRewards(false);
    }
  }

  loyalFocus() {
    if (this.$appStore.state.ui.bag.visible) {
      this.$services.ui.nextFocus('pickup bag');
    } else {
      this.$services.ui.focusBack();
    }
  }

  private closeModalOnEscapeEventHandler(event: KeyboardEvent) {
    if (this.visible && event.key == 'Escape') {
        const detailView = document.getElementsByClassName('details-view');
        const modalState = this.$appStore.state.ui.modals;

        // ordering matters here since multiple modals can display at once
        // if the ordering is wrong, a background modal will close instead of the foreground model
      if (modalState.basicError.visible) {
        // error modal first, its probably on top of everything
        this.$services.ui.setModalVisibility('basicError', false);

        const { args } = modalState.basicError;
        if (args && args.onClose) {
          args.onClose();  
        }

        this.$services.ui.focusBack();
      } else if (detailView.length > 0) {
        if (modalState.favoriteRestaurant.visible) {
          this.$services.ui.setModalVisibility('favoriteRestaurant', false);
        }
      } else if (modalState.editCountryModal.visible) {
        this.$services.ui.setModalVisibility('editCountryModal', false);
        this.$services.ui.focusBack();
      } else if (modalState.loyaltyExplainer.visible) {
        this.$services.ui.setModalVisibility('loyaltyExplainer', false);
        this.loyalFocus();
      } else if (modalState.rewardsCelebration.visible) {
        this.$services.ui.setModalVisibility('rewardsCelebration', false);
        if (this.$appStore.state.ui.bag.visible) {
          this.$services.ui.nextFocus('child-of-order-bag');
        } else {
          const focusNearby = document.getElementsByClassName('customer-greeting')[0] as HTMLElement;
          if (focusNearby) {
            focusNearby.focus();
          }
        }
      } else if (modalState.mealBuilderMealName.visible) {
        this.$services.ui.setModalVisibility('mealBuilderMealName', false);
        this.$services.ui.focusBack();
      } else if (modalState.fullOrderDetails.visible) {
        this.$services.ui.setModalVisibility('fullOrderDetails', false);
        this.$services.ui.focusBack();
      } else if (modalState.favoriteRestaurant.visible) {
        this.$services.ui.setModalVisibility('favoriteRestaurant', false);
        this.$services.ui.focusBack();
      } else if (modalState.deliveryAddress.visible) {
        this.$services.ui.setModalVisibility('deliveryAddress', false);
        this.$services.ui.focusBack();
      } else if (modalState.epop.visible) {
        this.$services.ui.setModalVisibility('epop', false);
      } else if (modalState.retroCreditConfirmNavModal.visible) {
        this.$services.ui.setModalVisibility('retroCreditConfirmNavModal', false);                
        this.$services.ui.nextFocus('step-num');
      } else if (modalState.cancelGroupOrder.visible) {
        this.$services.ui.setModalVisibility('cancelGroupOrder', false);
        this.$services.ui.focusBack();
      } else {
        this.close();
      }
    }
  }
}
